/**
 * Admin panel for online store orders (path: /online-orders).
 *
 * Visible to super_admin and to any user with the `manage_online_orders`
 * capability. Shows a filterable table of orders pulled from `online_orders`
 * (created by the public /checkout flow) and a detail dialog where the
 * operator can change the fulfillment status (new → preparing → shipped →
 * delivered), add a tracking number/carrier and internal notes.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Storefront, Package, Truck, CheckCircle, XCircle, MapPin, User, Phone,
  EnvelopeSimple, Receipt, CreditCard, ArrowsClockwise, WhatsappLogo, SpeakerHigh, SpeakerSlash, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { playOrderChime, unlockAudio } from "@/lib/alertSound";
import { SOUND_KEY, soundEnabled } from "@/components/StoreOrderAlerts";

const waLink = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? "52" + digits : digits}`;
};

const FULFILLMENT = [
  { v: "new",        label: "Nuevo",        color: "#009EE3", Icon: Storefront },
  { v: "preparing",  label: "Preparando",   color: "#D9A93B", Icon: Package },
  { v: "shipped",    label: "Enviado",      color: "#7E57C2", Icon: Truck },
  { v: "delivered",  label: "Entregado",    color: "#00873D", Icon: CheckCircle },
  { v: "cancelled",  label: "Cancelado",    color: "#E0000F", Icon: XCircle },
];

const PAYMENT_STATUS = {
  paid:       { label: "Pagado",       color: "#00873D" },
  pending:    { label: "Pendiente",    color: "#D9A93B" },
  rejected:   { label: "Rechazado",    color: "#E0000F" },
  cancelled:  { label: "Cancelado",    color: "#E0000F" },
  unknown:    { label: "—",            color: "#999" },
};

const fmtMXN = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }) : "—";

export default function OnlineOrders() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // paid_pending|paid|pending|all — "all" por defecto para no perder intentos de pago
  const [detail, setDetail] = useState(null);
  const [sound, setSound] = useState(soundEnabled());

  const toggleSound = () => {
    const next = !sound;
    localStorage.setItem(SOUND_KEY, next ? "on" : "off");
    setSound(next);
    toast.success(next ? "Sonido de pedidos activado" : "Sonido de pedidos silenciado");
    if (next) unlockAudio().then(() => playOrderChime({ repeats: 1 })).catch(() => {});
  };
  const testSound = async () => {
    await unlockAudio();
    const ok = await playOrderChime({ repeats: 2 });
    if (!ok) toast.error("El navegador bloqueó el audio. Haz clic en la página e intenta de nuevo.");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : filter === "paid_pending"
        ? { status: "paid" }
        : filter === "paid" ? { status: "paid" }
        : filter === "pending" ? { status: "pending" } : {};
      const [{ data: ords }, { data: st }] = await Promise.all([
        api.get("/online-orders", { params }),
        api.get("/online-orders/stats"),
      ]);
      let filtered = ords;
      if (filter === "paid_pending") {
        filtered = ords.filter((o) => ["new", "preparing"].includes(o.fulfillment_status || "new"));
      }
      setOrders(filtered);
      setStats(st);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando pedidos");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const visibleCount = orders.length;

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">TIENDA ONLINE</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Pedidos</h1>
          <p className="text-zinc-500 mt-1 text-sm">{visibleCount} visibles</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={toggleSound} variant="outline" className={`gap-2 ${sound ? "border-[#002FA7] text-[#002FA7]" : "text-zinc-500"}`} data-testid="orders-sound-toggle" title="Alerta sonora cuando alguien compra o intenta pagar">
            {sound ? <SpeakerHigh size={14} weight="bold" /> : <SpeakerSlash size={14} weight="bold" />}
            {sound ? "Sonido activado" : "Sonido apagado"}
          </Button>
          <Button onClick={testSound} variant="ghost" size="sm" className="text-xs text-zinc-500 hover:text-[#002FA7]" data-testid="orders-sound-test">Probar sonido</Button>
          <Button onClick={load} variant="outline" className="gap-2" data-testid="reload-orders">
            <ArrowsClockwise size={14} /> Refrescar
          </Button>
        </div>
      </header>
      <p className="text-xs text-zinc-500 -mt-3" data-testid="orders-alert-hint">
        Mientras tengas el sistema abierto en cualquier pantalla, sonará una campana y verás un aviso cuando alguien pague, intente pagar o un pago sea rechazado.
      </p>

      {/* Stat tiles */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="online-orders-stats">
          <StatTile label="Pagadas pendientes de envío" value={stats.paid_pending_fulfillment} color="#009EE3" />
          <StatTile label="Pagadas totales" value={stats.paid_total} color="#00873D" />
          <StatTile label="Pago pendiente (OXXO/SPEI)" value={stats.pending_payment} color="#D9A93B" />
          <StatTile label="Rechazadas / canceladas" value={stats.rejected_or_cancelled} color="#E0000F" />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-mono-data uppercase tracking-wider text-zinc-500">Filtro:</span>
        {[
          { v: "paid_pending", label: "Por preparar/enviar", color: "#009EE3" },
          { v: "paid", label: "Todas pagadas", color: "#00873D" },
          { v: "pending", label: "Pago pendiente", color: "#D9A93B" },
          { v: "all", label: "Todas", color: "#002FA7" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setFilter(t.v)}
            className={`px-3 py-1 text-xs font-medium rounded-sm border transition-all ${
              filter === t.v ? "border-transparent text-white" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"
            }`}
            style={filter === t.v ? { background: t.color } : {}}
            data-testid={`filter-${t.v}`}
          >{t.label}</button>
        ))}
      </div>

      {/* Table */}
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="online-orders-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Cliente</th>
              <th className="text-left px-4 py-3">Ciudad</th>
              <th className="text-left px-4 py-3">Items</th>
              <th className="text-left px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Pago</th>
              <th className="text-left px-4 py-3">Estado envío</th>
              <th className="text-left px-4 py-3">Folio</th>
              <th className="text-left px-4 py-3">Fecha</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-400">Cargando…</td></tr>
            )}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-400 dotted-bg">Sin pedidos para mostrar</td></tr>
            )}
            {!loading && orders.map((o) => {
              const ps = PAYMENT_STATUS[o.status] || PAYMENT_STATUS.unknown;
              const fs = FULFILLMENT.find((f) => f.v === (o.fulfillment_status || "new")) || FULFILLMENT[0];
              const cust = o.customer || {};
              const city = o.shipping_address_structured?.city || "—";
              return (
                <tr key={o.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => setDetail(o)} data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900" data-testid={`order-customer-${o.id}`}>{cust.name || <span className="text-zinc-400 italic">Sin nombre</span>}</div>
                    {cust.phone && <div className="text-xs text-zinc-500 font-mono-data inline-flex items-center gap-1" data-testid={`order-phone-${o.id}`}><Phone size={10} /> {cust.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-700">{city}</td>
                  <td className="px-4 py-3 text-zinc-700">{(o.items || []).reduce((s, i) => s + (i.qty || 0), 0)}</td>
                  <td className="px-4 py-3 font-semibold numeric">{fmtMXN(o.total_mxn)}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-1 rounded-sm" style={{ background: ps.color + "20", color: ps.color }}>{ps.label}</span>
                    {o.status !== "paid" && o.mp_status_detail_label && (
                      <div className="text-[10px] text-zinc-500 mt-1 max-w-[180px] leading-tight" data-testid={`order-reason-${o.id}`}>{o.mp_status_detail_label}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1" style={{ background: fs.color + "20", color: fs.color }}>
                      <fs.Icon size={11} weight="bold" /> {fs.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono-data text-xs">{o.sale_folio ? `#${o.sale_folio}` : "—"}</td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{fmtDate(o.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <OrderDetail order={detail} onClose={() => setDetail(null)} onUpdated={(o) => { setDetail(o); load(); }} />
    </div>
  );
}

function StatTile({ label, value, color }) {
  return (
    <div className="border border-zinc-200 bg-white p-4 rounded">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className="font-heading text-3xl font-extrabold mt-1" style={{ color }}>{value ?? 0}</div>
    </div>
  );
}

function OrderDetail({ order, onClose, onUpdated }) {
  const [form, setForm] = useState({ fulfillment_status: "new", tracking_number: "", carrier: "", internal_notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order) {
      setForm({
        fulfillment_status: order.fulfillment_status || "new",
        tracking_number: order.tracking_number || "",
        carrier: order.carrier || "",
        internal_notes: order.internal_notes || "",
      });
    }
  }, [order]);

  const fs = useMemo(
    () => FULFILLMENT.find((f) => f.v === form.fulfillment_status) || FULFILLMENT[0],
    [form.fulfillment_status],
  );

  const save = async () => {
    if (!order) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/online-orders/${order.id}`, form);
      toast.success("Pedido actualizado");
      onUpdated && onUpdated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  if (!order) return null;
  const cust = order.customer || {};
  const ps = PAYMENT_STATUS[order.status] || PAYMENT_STATUS.unknown;
  const addr = order.shipping_address_structured || {};

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            Pedido {order.sale_folio ? `#${order.sale_folio}` : order.id.slice(0, 8)}
            <span className="text-xs font-semibold px-2 py-1 rounded-sm" style={{ background: ps.color + "20", color: ps.color }}>{ps.label}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Detalle del pedido de la tienda: cliente, dirección, pago, productos y estado de entrega.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT: Customer + Address */}
          <div className="space-y-4">
            <Section title="Cliente" Icon={User}>
              <Row label="Nombre" value={cust.name || "Sin nombre registrado"} bold />
              <Row label="Teléfono" value={cust.phone || "Sin teléfono"} Icon={Phone} mono />
              <Row label="Correo" value={cust.email || "Sin correo"} Icon={EnvelopeSimple} />
              {(cust.phone || cust.email) && (
                <div className="flex gap-2 flex-wrap pt-1" data-testid="customer-contact-actions">
                  {waLink(cust.phone) && (
                    <a href={waLink(cust.phone)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-sm bg-[#25D366] text-white hover:opacity-90" data-testid="contact-whatsapp">
                      <WhatsappLogo size={14} weight="fill" /> WhatsApp
                    </a>
                  )}
                  {cust.phone && (
                    <a href={`tel:${String(cust.phone).replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-sm border border-zinc-300 text-zinc-800 hover:bg-zinc-50" data-testid="contact-call">
                      <Phone size={14} weight="bold" /> Llamar
                    </a>
                  )}
                  {cust.email && (
                    <a href={`mailto:${cust.email}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-sm border border-zinc-300 text-zinc-800 hover:bg-zinc-50" data-testid="contact-email">
                      <EnvelopeSimple size={14} weight="bold" /> Correo
                    </a>
                  )}
                </div>
              )}
            </Section>

            <Section title="Dirección de envío" Icon={MapPin}>
              <p className="text-sm text-zinc-700">{order.shipping_address}</p>
              {addr.zip && <p className="text-xs text-zinc-500 mt-1">CP {addr.zip}</p>}
            </Section>

            <Section title="Pago" Icon={CreditCard}>
              <Row label="Método" value={order.mp_payment_method || order.mp_payment_type || "—"} />
              <Row label="ID Mercado Pago" value={order.mp_payment_id} mono />
              <Row label={order.status === "paid" ? "Total cobrado" : "Total del pedido"} value={fmtMXN(order.total_mxn)} bold />
              {order.status !== "paid" && (order.mp_status_detail_label || order.error) && (
                <div className="mt-2 flex items-start gap-2 text-xs rounded-sm border border-red-200 bg-red-50 text-red-800 p-2" data-testid="payment-reason">
                  <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">{order.mp_status_detail_label || "Pago no completado"}</div>
                    {order.error && <div className="text-[11px] text-red-700/80 mt-0.5">{order.error}</div>}
                    {order.mp_status_detail && <div className="text-[10px] font-mono-data text-red-700/60 mt-0.5">{order.mp_status_detail}</div>}
                  </div>
                </div>
              )}
            </Section>
          </div>

          {/* RIGHT: Items + Fulfillment */}
          <div className="space-y-4">
            <Section title="Productos" Icon={Receipt}>
              <ul className="space-y-2">
                {(order.items || []).map((it, idx) => (
                  <li key={idx} className="flex gap-2 text-sm">
                    <div className="w-10 h-10 bg-zinc-50 border border-zinc-100 rounded shrink-0 overflow-hidden flex items-center justify-center">
                      {it.photo ? <img src={it.photo} alt={it.product_name} className="w-full h-full object-contain" /> : <span className="text-[9px] text-zinc-300">—</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="line-clamp-2 leading-tight">{it.product_name}</p>
                      <p className="text-[11px] text-zinc-500 numeric">{it.qty} × {fmtMXN(it.unit_price_mxn)}</p>
                    </div>
                    <span className="text-sm font-semibold numeric whitespace-nowrap">{fmtMXN(it.subtotal_mxn)}</span>
                  </li>
                ))}
              </ul>
              <div className="text-xs text-zinc-500 mt-3 pt-3 border-t border-zinc-100">
                Subtotal: <span className="numeric">{fmtMXN(order.items_subtotal_mxn)}</span> · Envío: <span className="numeric">{fmtMXN(order.shipping_cost_mxn)}</span>
              </div>
            </Section>

            <Section title="Estado de entrega" Icon={fs.Icon}>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Estado</Label>
                  <div className="mt-1.5 grid grid-cols-5 gap-1.5">
                    {FULFILLMENT.map((f) => {
                      const active = form.fulfillment_status === f.v;
                      return (
                        <button
                          key={f.v}
                          type="button"
                          onClick={() => setForm({ ...form, fulfillment_status: f.v })}
                          className={`px-2 py-2 text-[10px] font-semibold rounded border transition-all ${active ? "text-white border-transparent" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                          style={active ? { background: f.color } : {}}
                          data-testid={`set-fulfillment-${f.v}`}
                        >
                          <f.Icon size={14} weight="bold" className="mx-auto mb-1" />
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {(form.fulfillment_status === "shipped" || form.fulfillment_status === "delivered") && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Paquetería</Label>
                      <Input
                        value={form.carrier}
                        onChange={(e) => setForm({ ...form, carrier: e.target.value })}
                        placeholder="DHL, Estafeta, FedEx…"
                        className="mt-1 text-sm"
                        data-testid="carrier-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Número de guía</Label>
                      <Input
                        value={form.tracking_number}
                        onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
                        placeholder="1234567890"
                        className="mt-1 text-sm font-mono-data"
                        data-testid="tracking-input"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs uppercase tracking-wider">Notas internas</Label>
                  <textarea
                    value={form.internal_notes}
                    onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                    rows={3}
                    placeholder="Observaciones del pedido (no visibles para el cliente)…"
                    className="mt-1 w-full p-2 text-sm border border-zinc-300 rounded resize-none focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
                    data-testid="internal-notes"
                  />
                </div>

                {order.fulfillment_history && order.fulfillment_history.length > 0 && (
                  <details className="text-xs text-zinc-500">
                    <summary className="cursor-pointer hover:text-zinc-700">Historial ({order.fulfillment_history.length})</summary>
                    <ul className="mt-2 space-y-1">
                      {order.fulfillment_history.slice().reverse().map((h, i) => (
                        <li key={i} className="border-l-2 border-zinc-200 pl-2 py-0.5">
                          <span className="font-semibold">{FULFILLMENT.find((f) => f.v === h.status)?.label || h.status}</span>
                          {" · "}{fmtDate(h.at)}{" · "}{h.user_email}
                          {h.tracking_number && <span className="font-mono-data"> · {h.carrier || "Guía"} {h.tracking_number}</span>}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                <Button onClick={save} disabled={saving} className="w-full bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="save-order">
                  {saving ? "Guardando…" : "Guardar cambios"}
                </Button>
              </div>
            </Section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, Icon, children }) {
  return (
    <div className="border border-zinc-200 rounded p-4 bg-white">
      <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-3 inline-flex items-center gap-1.5">
        {Icon && <Icon size={12} weight="bold" />} {title}
      </h3>
      {children}
    </div>
  );
}

function Row({ label, value, Icon, mono, bold }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm py-1">
      {Icon && <Icon size={12} className="text-zinc-400 mt-1 shrink-0" />}
      <span className="text-zinc-500 w-24 shrink-0 text-xs uppercase tracking-wider font-mono">{label}</span>
      <span className={`${mono ? "font-mono-data text-xs" : ""} ${bold ? "font-semibold" : ""} text-zinc-800 break-all`}>{value}</span>
    </div>
  );
}
